"use client";

import * as React from "react";
import { cn } from "@repo/utils/cn";
import { AnimatePresence, motion } from "motion/react";
import { Dropdown, DropdownTrigger, DropdownOption } from "../Dropdown";
import { SearchField } from "../SearchField";
import { CloseIcon } from "../icons/CloseIcon";
import { FilterIcon } from "../icons/FilterIcon";

export type FilterField = {
  type: "dropdown" | "search";
  name: string;
  placeholder?: string;
  options?: DropdownOption[];
};

export type FilterValues = Record<string, string>;
export type FilterChangeHandler = (fieldName: string, value: string) => void;

export interface FilterSectionMobileProps {
  id?: string;
  fields?: FilterField[];
  className?: string;
  inputBackground?: "default" | "white";
  initialValues?: FilterValues;
  onFiltersChange?: (filters: FilterValues) => void;
  onFieldChange?: FilterChangeHandler;
  activeFilterCount?: number;
  showBorder?: boolean;
}

export const FilterSectionMobile = React.forwardRef<
  HTMLDivElement,
  FilterSectionMobileProps
>(
  (
    {
      fields = [],
      className,
      inputBackground = "default",
      initialValues = {},
      onFiltersChange,
      onFieldChange,
      activeFilterCount = 0,
      showBorder = true,
      id,
    },
    ref,
  ) => {
    const [isExpanded, setIsExpanded] = React.useState(false);
    const [filterValues, setFilterValues] =
      React.useState<FilterValues>(initialValues);

    React.useEffect(() => {
      setFilterValues(initialValues);
    }, [initialValues]);

    const handleFieldChange: FilterChangeHandler = React.useCallback(
      (fieldName: string, value: string) => {
        setFilterValues((prev) => {
          const newValues = { ...prev, [fieldName]: value };
          onFieldChange?.(fieldName, value);
          onFiltersChange?.(newValues);
          return newValues;
        });
      },
      [onFiltersChange, onFieldChange],
    );

    const renderField = (field: FilterField, index: number) => {
      const fieldValue = filterValues[field.name] || "";

      const zIndex = 50 - index;

      if (field.type === "dropdown") {
        const selectedOption = fieldValue
          ? field.options?.find((opt) => opt.value === fieldValue)
          : undefined;

        return (
          <div key={field.name} className="uipm:relative" style={{ zIndex }}>
            <Dropdown
              options={field.options || []}
              value={fieldValue}
              onValueChange={(value) => handleFieldChange(field.name, value)}
              showNoneOption={true}
              noneOptionLabel={field.placeholder || "None selected"}
              contentClassName="uipm:z-[100]"
              trigger={
                <DropdownTrigger
                  placeholder={field.placeholder || "Select"}
                  selectedLabel={selectedOption?.label}
                  background={inputBackground}
                  className="uipm:w-full uipm:bg-[var(--surface-surface-secondary)] uipm:p-3 uipm:rounded-lg uipm:min-h-[48px]"
                />
              }
            />
          </div>
        );
      }

      return (
        <div key={field.name} className="uipm:relative" style={{ zIndex }}>
          <SearchField
            placeholder={field.placeholder || "Search"}
            value={fieldValue}
            background={inputBackground}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
            onSearch={(value) => handleFieldChange(field.name, value)}
            containerClassName="uipm:w-full"
          />
        </div>
      );
    };

    const activeCount = React.useMemo(() => {
      return Object.values(filterValues).filter(
        (value) => value && value !== "",
      ).length;
    }, [filterValues]);

    return (
      <div
        ref={ref}
        id={id}
        className={cn(
          "uipm:bg-[var(--surface-surface-primary)]",
          showBorder && "uipm:border-b uipm:border-[var(--color-color-light)]",
          className,
        )}
      >
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="uipm:w-full uipm:px-[var(--container-margin)] uipm:py-3 uipm:flex uipm:items-center uipm:justify-between uipm:bg-transparent uipm:border-0 uipm:cursor-pointer"
        >
          <div className="uipm:flex uipm:items-center uipm:gap-2">
            {isExpanded ? (
              <CloseIcon
                className="uipm:w-6 uipm:h-6"
                color="var(--surface-surface-accent)"
              />
            ) : (
              <FilterIcon
                className="uipm:w-6 uipm:h-6"
                color="var(--surface-surface-accent)"
              />
            )}
            <span className="uipm:text-[color:var(--typography-colors-text-accent)] uipm:text-[length:var(--typography-sizes-body-medium,16px)] uipm:font-medium uipm:leading-[var(--typography-line-heights-body-medium,26px)] uipm:font-[family-name:Inter]">
              Filters
            </span>
            {activeCount > 0 && (
              <span className="uipm:min-w-[20px] uipm:h-[20px] uipm:flex uipm:items-center uipm:justify-center uipm:px-1.5 uipm:bg-[var(--surface-surface-accent)] uipm:text-[var(--typography-colors-text-inverted)] uipm:rounded-full uipm:text-xs uipm:font-bold">
                {activeCount}
              </span>
            )}
          </div>
        </button>

        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2, ease: "easeInOut" }}
              className="uipm:overflow-visible"
              style={{ overflow: "visible" }}
            >
              <div className="uipm:px-[var(--container-margin)] uipm:py-4 uipm:overflow-visible">
                {/* Filter Fields */}
                <div className="uipm:space-y-3 uipm:overflow-visible">
                  {fields.map((field, index) => renderField(field, index))}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  },
);

FilterSectionMobile.displayName = "FilterSectionMobile";
